// Predefined workflows for the Workflow Builder — the recurring multi-step jobs from
// the feature doc. Each workflow generates a fixed set of suggested tasks the user can
// bulk-assign / date / trim before creating, mirroring the Kickoff Builder.
//
// Each task: { title, description, service_area, approval_path }.
//   service_area  → one of constants.SERVICE_AREAS values
//   approval_path → 'internal_client_live' (client-facing deliverables) or 'internal'
//                   (internal research / removals). These are sensible defaults — they
//                   can be changed per task after the tasks are created.
//
// Edit freely: add a workflow object to WORKFLOWS, or add/remove tasks in any list.

export const WORKFLOWS = [
  {
    id: 'new_therapist',
    label: 'A New Therapist',
    description: 'Onboard a newly added clinician across the website, social, email, ads, and SEO.',
    tasks: [
      { title: 'Add the new therapist to the website', description: 'Typically an individual bio page plus a card on the team page.', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Create a social media post introducing the therapist', description: '', service_area: 'social', approval_path: 'internal_client_live' },
      { title: 'Create a newsletter introducing the therapist', description: '', service_area: 'email', approval_path: 'internal_client_live' },
      { title: 'Review Google Ads for the new clinician’s caseload', description: 'Consider new ad groups or budget changes related to their caseload.', service_area: 'ads', approval_path: 'internal_client_live' },
      { title: 'Consider new service pages & SEO for the therapist’s specialties', description: '', service_area: 'seo', approval_path: 'internal_client_live' },
    ],
  },
  {
    id: 'therapist_leaves',
    label: 'Therapist Leaves the Practice',
    description: 'Remove a departing clinician from the website and wind down anything specific to them.',
    tasks: [
      { title: 'Remove the therapist from the website (bio page)', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Remove the therapist from the team page', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Disable ad groups specific to this therapist only', description: 'Only items that were specific to this therapist.', service_area: 'ads', approval_path: 'internal' },
      { title: 'Remove or redirect SEO pages specific to this therapist only', description: 'Only items that were specific to this therapist.', service_area: 'seo', approval_path: 'internal' },
    ],
  },
  {
    id: 'new_service',
    label: 'Practice Launches a New Service',
    description: 'Stand up a new service across landing pages, ads, SEO, social, and email.',
    tasks: [
      { title: 'Build a new landing page for the service', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Set up new ad groups for the service', description: '', service_area: 'ads', approval_path: 'internal_client_live' },
      { title: 'Create new SEO page(s) for the service', description: '', service_area: 'seo', approval_path: 'internal_client_live' },
      { title: 'Announce the new service on social media', description: '', service_area: 'social', approval_path: 'internal_client_live' },
      { title: 'Add the new service to the newsletter', description: '', service_area: 'email', approval_path: 'internal_client_live' },
    ],
  },
  {
    id: 'ends_service',
    label: 'Practice Ends a Service',
    description: 'Wind down a service the practice no longer provides.',
    tasks: [
      { title: 'Remove the service pages from the website', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Pause ad groups for the service (if running)', description: '', service_area: 'ads', approval_path: 'internal' },
    ],
  },
  {
    id: 'adds_location',
    label: 'Practice Adds a Location',
    description: 'Launch a new location (virtual or brick-and-mortar) across the site, SEO, ads, social, and email.',
    tasks: [
      { title: 'Add a location page', description: 'Virtual or brick-and-mortar — adjust the page accordingly.', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'SEO / keyword research for the location page (and any other needed pages)', description: '', service_area: 'seo', approval_path: 'internal' },
      { title: 'Add the location to the footer, contact page, and anywhere location info is listed', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Expand Google Ads for the location (or start a new campaign)', description: '', service_area: 'ads', approval_path: 'internal_client_live' },
      { title: 'Announce the new location on social media', description: '', service_area: 'social', approval_path: 'internal_client_live' },
      { title: 'Announce the new location in a newsletter', description: '', service_area: 'email', approval_path: 'internal_client_live' },
      { title: 'Review FAQs for a place to add the location', description: '', service_area: 'website', approval_path: 'internal' },
    ],
  },
  {
    id: 'closes_location',
    label: 'Practice Closes a Location',
    description: 'Wind down a location (virtual or brick-and-mortar) — the inverse of adding one.',
    tasks: [
      { title: 'Remove or redirect the location page', description: 'Virtual or brick-and-mortar.', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Remove the location from the footer, contact page, and other listings', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Pause or adjust Google Ads for the closed location', description: '', service_area: 'ads', approval_path: 'internal' },
      { title: 'Announce the closure on social media (if appropriate)', description: '', service_area: 'social', approval_path: 'internal_client_live' },
      { title: 'Announce the closure in a newsletter (if appropriate)', description: '', service_area: 'email', approval_path: 'internal_client_live' },
      { title: 'Update FAQs to remove the location', description: '', service_area: 'website', approval_path: 'internal' },
    ],
  },
  {
    id: 'adds_insurance',
    label: 'Practice Adds an Insurance Carrier',
    description: 'Add a newly accepted insurance carrier across the site, social, email, and ads.',
    tasks: [
      { title: 'Add the carrier to any FAQs that mention insurance', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Add the carrier to the insurance page (if one exists)', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Add the carrier to insurance call-outs on landing pages', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Announce the new carrier on social media', description: '', service_area: 'social', approval_path: 'internal_client_live' },
      { title: 'Announce the new carrier in a newsletter', description: '', service_area: 'email', approval_path: 'internal_client_live' },
      { title: 'Add the carrier to ads when appropriate', description: 'Note: some carriers (e.g. Aetna) may be flagged for trademark; others can be named in copy, keywords, and on the landing page (e.g. “now accepting Blue Cross Blue Shield”).', service_area: 'ads', approval_path: 'internal_client_live' },
    ],
  },
  {
    id: 'new_adgroup_lp',
    label: 'New Ad Group / Landing Page',
    description: 'Add a new ad group and landing page to an existing Google campaign.',
    tasks: [
      { title: 'Keyword research', description: '', service_area: 'ads', approval_path: 'internal' },
      { title: 'Build the landing page', description: '', service_area: 'website', approval_path: 'internal_client_live' },
      { title: 'Add the ad group to the existing campaign', description: '', service_area: 'ads', approval_path: 'internal' },
    ],
  },
  {
    id: 'therapist_qas',
    label: 'Therapist Q&As',
    description: 'Turn a therapist Q&A into a carousel, blog, newsletter, and bio link.',
    tasks: [
      { title: 'Choose therapist(s) for the Q&A', description: '', service_area: 'account_management', approval_path: 'internal' },
      { title: 'Gather questions based on their key specialties', description: '', service_area: 'copywriting', approval_path: 'internal' },
      { title: 'Send the questions to the client', description: '', service_area: 'account_management', approval_path: 'internal' },
      { title: 'Create a carousel post from the answers', description: '', service_area: 'social', approval_path: 'internal_client_live' },
      { title: 'Write a blog post from the Q&A', description: '', service_area: 'copywriting', approval_path: 'internal_client_live' },
      { title: 'Add the Q&A and a link to the blog to a newsletter', description: '', service_area: 'email', approval_path: 'internal_client_live' },
      { title: 'Add the Q&A link to the therapist’s bio', description: '', service_area: 'website', approval_path: 'internal_client_live' },
    ],
  },
]

export const getWorkflow = id => WORKFLOWS.find(w => w.id === id)